/**
 * ExpressCheckoutSection - Express checkout options (Apple Pay, Google Pay, etc.)
 *
 * Wraps Stripe ExpressCheckoutElement with consistent styling and divider
 */

import { ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js';

interface ExpressCheckoutSectionProps {
  /** Callback when express checkout is confirmed */
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  /** Whether express checkout is disabled */
  disabled?: boolean;
}

export function ExpressCheckoutSection({
  onConfirm,
  disabled,
}: ExpressCheckoutSectionProps) {
  const stripe = useStripe();
  const elements = useElements();

  // Don't render if Stripe isn't ready yet
  if (!stripe || !elements) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Express checkout buttons */}
      <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
        <ExpressCheckoutElement
          onConfirm={onConfirm}
          onClick={(event) => {
            event.resolve({
              emailRequired: true,
              billingAddressRequired: true,
              business: {
                name: 'Catch',
              },
            });
          }}
          options={
            {
              buttonHeight: 50,
              buttonType: {
                applePay: 'plain',
                googlePay: 'plain',
              },
              paymentMethodOrder: ['apple_pay', 'google_pay'],
              layout: {
                maxColumns: 1,
                maxRows: 0,
                overflow: 'never',
              },
              // paymentMethods is a valid option per Stripe docs but missing from older type definitions
              // https://docs.stripe.com/elements/express-checkout-element
              paymentMethods: {
                applePay: 'always',
                googlePay: 'always',
              },
            } as StripeExpressCheckoutElementOptions
          }
        />
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-sm text-slate-400">Or pay with card</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
    </div>
  );
}

export default ExpressCheckoutSection;
